import { useState } from 'react';
import useSWR from 'swr';
import { useDebounceValue } from 'usehooks-ts';
import { toast } from 'App';
import {
  CreatePermissionGate,
  DeletePermissionGate,
  UpdatePermissionGate,
} from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { adminService } from 'views/services/adminService';
import { invoicePaymentsService } from 'views/services/invoicePaymentsService';
import { IInvoiceSector } from 'views/types/invoicePayments';
import {
  cellClass,
  dangerSmallBtn,
  headerClass,
  inputCls,
  labelCls,
  primaryBtn,
  secondaryBtn,
  smallBtn,
} from './shared';

const PAGE_SIZE = 50;

const TIER_LABELS: Record<string, string> = {
  low_risk: 'Low risk',
  medium_risk: 'Medium risk',
  high_risk: 'High risk (enhanced review)',
  prohibited: 'Prohibited (blocked)',
};

const tierClass = (tier: string) =>
  tier === 'prohibited'
    ? 'text-red-500'
    : tier === 'high_risk'
      ? 'text-amber-500'
      : 'text-green-600';

interface IDraft {
  category: string;
  subcategory: string;
  risk_tier: IInvoiceSector['risk_tier'];
}

const EMPTY: IDraft = { category: '', subcategory: '', risk_tier: 'low_risk' };

export const SectorsPanel = () => {
  const [search, setSearch] = useState('');
  const [searchDebounce] = useDebounceValue(search, 600);
  const [riskTier, setRiskTier] = useState('');
  const [page, setPage] = useState(0);
  const [draft, setDraft] = useState<IDraft>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data, mutate } = useSWR(
    [
      '/api/admin/invoice-payment-config/sectors',
      searchDebounce,
      riskTier,
      page,
    ],
    () =>
      invoicePaymentsService.getSectors({
        search: searchDebounce || undefined,
        riskTier: riskTier || undefined,
        page: page + 1,
        limit: PAGE_SIZE,
      })
  );

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const startEdit = (sector: IInvoiceSector) => {
    setEditingId(sector.id);
    setDraft({
      category: sector.category,
      subcategory: sector.subcategory,
      risk_tier: sector.risk_tier,
    });
  };

  const submit = async () => {
    if (!draft.category.trim()) {
      toast({ title: 'Category is required', status: 'error' });
      return;
    }
    const payload = {
      category: draft.category.trim(),
      subcategory: draft.subcategory.trim(),
      risk_tier: draft.risk_tier,
    };
    try {
      setBusy(true);
      if (editingId) {
        await invoicePaymentsService.updateSector(editingId, payload);
        toast({ title: 'Sector updated', status: 'success' });
      } else {
        await invoicePaymentsService.createSector(payload);
        toast({ title: 'Sector created', status: 'success' });
      }
      setDraft(EMPTY);
      setEditingId(null);
      await mutate();
    } catch (error) {
      adminService.errorHandler(error);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this sector?')) return;
    try {
      setBusy(true);
      await invoicePaymentsService.deleteSector(id);
      toast({ title: 'Sector deleted', status: 'success' });
      await mutate();
    } catch (error) {
      adminService.errorHandler(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <CreatePermissionGate resource={PERMISSION_RESOURCES.INVOICE_CONFIG}>
        <div className="mb-4 grid grid-cols-2 gap-3 rounded border border-gray-200 p-3 dark:border-white/20 md:grid-cols-4">
          <div>
            <label className={labelCls}>Category</label>
            <input
              className={inputCls}
              value={draft.category}
              onChange={e => setDraft({ ...draft, category: e.target.value })}
            />
          </div>
          <div>
            <label className={labelCls}>
              Subcategory (empty = category row)
            </label>
            <input
              className={inputCls}
              value={draft.subcategory}
              onChange={e =>
                setDraft({ ...draft, subcategory: e.target.value })
              }
            />
          </div>
          <div>
            <label className={labelCls}>Risk tier</label>
            <select
              className={inputCls}
              value={draft.risk_tier}
              onChange={e =>
                setDraft({
                  ...draft,
                  risk_tier: e.target.value as IDraft['risk_tier'],
                })
              }
            >
              {Object.entries(TIER_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button disabled={busy} onClick={submit} className={primaryBtn}>
              {editingId ? 'Save' : 'Add sector'}
            </button>
            {editingId && (
              <button
                className={secondaryBtn}
                onClick={() => {
                  setEditingId(null);
                  setDraft(EMPTY);
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </CreatePermissionGate>

      <div className="mb-3 flex gap-3">
        <input
          className="w-72 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:bg-navy-800 dark:text-white"
          placeholder="Search category / subcategory…"
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <select
          className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:bg-navy-800 dark:text-white"
          value={riskTier}
          onChange={e => {
            setRiskTier(e.target.value);
            setPage(0);
          }}
        >
          <option value="">All tiers</option>
          {Object.entries(TIER_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <span className="self-center text-sm text-gray-500">
          {total} sectors
        </span>
      </div>

      <table className="w-full">
        <thead>
          <tr>
            <th className={headerClass}>Category</th>
            <th className={headerClass}>Subcategory</th>
            <th className={headerClass}>Risk tier</th>
            <th className={headerClass}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(sector => (
            <tr key={sector.id}>
              <td className={cellClass}>{sector.category}</td>
              <td className={cellClass}>{sector.subcategory || '—'}</td>
              <td className={`${cellClass} ${tierClass(sector.risk_tier)}`}>
                {TIER_LABELS[sector.risk_tier] ?? sector.risk_tier}
              </td>
              <td className={`${cellClass} flex gap-2`}>
                <UpdatePermissionGate
                  resource={PERMISSION_RESOURCES.INVOICE_CONFIG}
                >
                  <button
                    className={smallBtn}
                    onClick={() => startEdit(sector)}
                  >
                    Edit
                  </button>
                </UpdatePermissionGate>
                <DeletePermissionGate
                  resource={PERMISSION_RESOURCES.INVOICE_CONFIG}
                >
                  <button
                    className={dangerSmallBtn}
                    disabled={busy}
                    onClick={() => remove(sector.id)}
                  >
                    Delete
                  </button>
                </DeletePermissionGate>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td
                className="py-4 text-center text-sm text-gray-500"
                colSpan={4}
              >
                No sectors found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="mt-4 flex items-center justify-between">
        <span className="text-sm text-gray-600 dark:text-white">
          page {page + 1} / {pageCount}
        </span>
        <div className="flex gap-2">
          <button
            disabled={page <= 0}
            onClick={() => setPage(p => Math.max(0, p - 1))}
            className={secondaryBtn}
          >
            Prev
          </button>
          <button
            disabled={page + 1 >= pageCount}
            onClick={() => setPage(p => p + 1)}
            className={secondaryBtn}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};
